import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IPhone } from 'rpc/model/squareup/buyerportal/buyer/data';
import { CardInfo } from 'rpc/model/squareup/buyerportal/common/data';
import {
  AccountInfo,
  ProgramInfo,
} from 'rpc/model/squareup/buyerportal/merchantportal/loyalty/data';
import { Identifier } from 'rpc/model/squareup/buyerportal/profile/common';
import { EmailCollectionIncentiveTermsAndStatus } from 'rpc/model/squareup/card/balance/loyalty_api/model/email-collection-incentive-terms-and-status';
import {
  LoyaltyProgramStatus,
  LoyaltyProgram,
} from 'rpc/model/squareup/card/balance/loyalty_api/model/loyalty-program';
import { LoyaltyPromotion } from 'rpc/model/squareup/card/balance/loyalty_api/model/loyalty-promotion';
import { MembershipProgram } from 'rpc/model/squareup/memberships/model/membership-program';
import { CreatePersonalizedLoyaltyPassUrlResponse } from 'rpc/model/squareup/passes/service/url-service';
import { AppState } from 'store';
import { LoyaltyTermsOfServiceState } from './models/LoyaltyTermsOfServiceState';

export type LoyaltyMerchantPortalState = {
  accountInfo?: AccountInfo;
  emailCollectionIncentiveTermsAndStatus?: EmailCollectionIncentiveTermsAndStatus;
  linkedPaymentCards: Array<CardInfo>;
  loyaltyAccountLookupToken?: string;
  passDownloadData?: CreatePersonalizedLoyaltyPassUrlResponse.PersonalizedPassDownloadData;
  programInfo?: ProgramInfo;
  promotions?: LoyaltyPromotion[];
  tosState?: LoyaltyTermsOfServiceState;
};

// Selector insulates consumers from the changes to the internal state shape
// (and adds opportunities for memoization)
export const selectAssociatedPhone = (
  state: LoyaltyMerchantPortalState
): string | undefined => {
  if (!state.loyaltyAccountLookupToken) {
    return undefined;
  }
  return state.loyaltyAccountLookupToken;
};

export const selectAccount = (
  state: LoyaltyMerchantPortalState
): AccountInfo | undefined => {
  return state.accountInfo;
};

export const selectEmailCollectionIncentiveTermsAndStatus = (
  state: LoyaltyMerchantPortalState
) => {
  return state.emailCollectionIncentiveTermsAndStatus;
};

// Returns the phone identifier associated with the loyalty account, if any
// If a list of verified buyer phones is provided and the list contains
// a phone matching the loyalty account's phone identifier, the verified
// phone is returned instead. This is preferred as it provides the raw
// phone number instead of an obfuscated version from the loyalty account.
export const selectLoyaltyPhoneIdentifier = (
  state: AppState,
  phones?: readonly IPhone[]
): Identifier | undefined => {
  let loyaltyPhoneIdentifier =
    state.loyaltyMerchantPortal?.accountInfo?.phoneIdentifier;

  if (!loyaltyPhoneIdentifier) {
    return undefined;
  }

  if (phones?.length) {
    const phone = phones.find(
      (phone) => phone.id === loyaltyPhoneIdentifier?.id
    );
    if (phone) {
      loyaltyPhoneIdentifier = Identifier.create({
        displayValue: phone.displayValue,
        id: phone.id,
        type: Identifier.Type.TYPE_PHONE,
      });
    }
  }
  return loyaltyPhoneIdentifier;
};

export const selectPassDownloadUrl = (state: LoyaltyMerchantPortalState) => {
  const passDownloadData = state.passDownloadData;

  if (!passDownloadData) {
    return null;
  }

  if (passDownloadData.isInstalled) {
    return null;
  }

  return passDownloadData.passDownloadUrl;
};

export const selectLinkedPaymentCards = (
  state: LoyaltyMerchantPortalState
): Array<CardInfo> => {
  return state.linkedPaymentCards;
};

export const selectLoyaltyProgram = (
  state: LoyaltyMerchantPortalState
): LoyaltyProgram | undefined => {
  return state.programInfo?.loyaltyProgram;
};

export const selectIsLoyaltyProgramActive = createSelector(
  (state: AppState) => selectLoyaltyProgram(state.loyaltyMerchantPortal),
  (loyaltyProgram) => loyaltyProgram?.status === LoyaltyProgramStatus.ACTIVE
);

export const selectMembershipProgram = (
  state: LoyaltyMerchantPortalState
): MembershipProgram | undefined => {
  return state.programInfo?.membershipProgram;
};

export const selectPointsTerminology = createSelector(
  (state: AppState) => selectLoyaltyProgram(state.loyaltyMerchantPortal),
  (loyaltyProgram) => loyaltyProgram?.terminology
);

export const selectPromotions = (
  state: LoyaltyMerchantPortalState
): LoyaltyPromotion[] | undefined => {
  return state.promotions;
};

export const selectTosState = (
  state: LoyaltyMerchantPortalState
): LoyaltyTermsOfServiceState | undefined => {
  return state.tosState;
};

export const initialState: LoyaltyMerchantPortalState = {
  accountInfo: undefined,
  linkedPaymentCards: [],
  loyaltyAccountLookupToken: undefined,
  passDownloadData: undefined,
  programInfo: undefined,
  promotions: [],
  tosState: undefined,
};

const loyaltyMerchantPortalSlice = createSlice({
  initialState,
  name: 'loyaltyMerchantPortal',
  reducers: {
    collectEmailIncentive(state) {
      const { accountInfo, emailCollectionIncentiveTermsAndStatus } = state;
      if (!emailCollectionIncentiveTermsAndStatus) {
        return;
      }
      if (typeof accountInfo?.loyaltyAccount?.balance === 'number') {
        accountInfo.loyaltyAccount.balance +=
          emailCollectionIncentiveTermsAndStatus.pointAmount;
      }

      emailCollectionIncentiveTermsAndStatus.eligible = false;
      Object.assign(state, {
        accountInfo,
        emailCollectionIncentiveTermsAndStatus,
      });
    },
    removePaymentCard(state, action: PayloadAction<string>) {
      const cardId = action.payload;
      const linkedPaymentCards = state.linkedPaymentCards.filter(
        (card) => card.id !== cardId
      );
      Object.assign(state, { linkedPaymentCards });
    },
    setAccountInfo(state, action: PayloadAction<AccountInfo>) {
      Object.assign(state, { accountInfo: action.payload });
    },
    setEmailCollectionIncentiveTermsAndStatus(
      state,
      action: PayloadAction<EmailCollectionIncentiveTermsAndStatus | undefined>
    ) {
      Object.assign(state, {
        emailCollectionIncentiveTermsAndStatus: action.payload,
      });
    },
    setLinkedPaymentCards(state, action: PayloadAction<Array<CardInfo>>) {
      Object.assign(state, {
        linkedPaymentCards: action.payload,
      });
    },
    setPassDownloadData(
      state,
      action: PayloadAction<
        | CreatePersonalizedLoyaltyPassUrlResponse.PersonalizedPassDownloadData
        | undefined
      >
    ) {
      Object.assign(state, { passDownloadData: action.payload });
    },
    setProgramInfo(state, action: PayloadAction<ProgramInfo>) {
      Object.assign(state, { programInfo: action.payload });
    },
    setPromotions(state, action: PayloadAction<LoyaltyPromotion[]>) {
      Object.assign(state, { promotions: action.payload });
    },
    setTosState(
      state,
      action: PayloadAction<LoyaltyTermsOfServiceState | undefined>
    ) {
      Object.assign(state, { tosState: action.payload });
    },
  },
});

export const {
  collectEmailIncentive,
  removePaymentCard,
  setAccountInfo,
  setEmailCollectionIncentiveTermsAndStatus,
  setLinkedPaymentCards,
  setPassDownloadData,
  setProgramInfo,
  setPromotions,
  setTosState,
} = loyaltyMerchantPortalSlice.actions;

export default loyaltyMerchantPortalSlice.reducer;
